import { Action } from 'redux';
import * as _ from 'underscore';

import { Tableau } from '../../common/utilities/TableauLog';
import { isSetNavLocationAction, SetNavLocationAction } from '../actions/spaces/SetNavLocationAction';
import { NavLocation, NavLocationAreas } from '../layout/NavLocation';

const InitialState: NavLocation = NavLocationAreas.Generic.unknown;

export function navLocationReducer(
  previousState: NavLocation = InitialState,
  action: Action
): NavLocation  {
  if (isSetNavLocationAction(action)) {

    let newNavLocation = previousState;

    // Special case for the react router state(s)
    if (action.routerStateName.includes('reactrouter')) {
      if (action.routerStateParams && _.has(action.routerStateParams, 'space')) {
        const stateKey = getReactRouterStateKey(action);
        newNavLocation = NavLocationFromMap(reactRouterStateParamSpaceToNavLocation, reactRouterStateParamSpaceToNavLocationKeys, stateKey);
      }
    } else {
      newNavLocation = NavLocationFromMap(routerStateNameToNavLocation, routerStateNameToNavLocationKeys, action.routerStateName);
    }

    if (newNavLocation !== previousState) {
      return newNavLocation;
    }
  }

  return previousState;
}

function getReactRouterStateKey(action: SetNavLocationAction) {
  let subspaceEnding = '';
  if (action.routerStateParams.subspace) {
    // when we get the subspace here, it usually has the id of the workbook/datasource/etc. and we don't want it. The last item
    // after the / is good enough to get us a unique subspace for the react router state <-> NavLocation map.
    subspaceEnding = (action.routerStateParams.subspace as string).split('/').pop();
  }

  // this is a temporary workaround to help disambiguate between paths to the metric subspaces and metric detail space
  // until metrics are integrated with the mixed content
  if (action.routerStateParams.space === 'metrics' &&
  !(subspaceEnding === undefined || subspaceEnding === 'favorites' || subspaceEnding === 'all' || subspaceEnding === 'owned')) {
    return 'metric_detail';
  }

  // Prepend server to the stateKey so we can differentiate between site and server pages.
  const prefix = (action.routerStateName.includes('server')) ? 'server.' : '';

  const stateKey = prefix + action.routerStateParams.space + '.' + subspaceEnding;
  return stateKey;
}

function NavLocationFromMap(navLocationMap: _.Dictionary<NavLocation>, navLocationKeys: string[], key: string) {
  const matchingStateName =
    _.find(navLocationKeys, (navLocationKey) => {
      return key.startsWith(navLocationKey);
    });

  if (!matchingStateName) {
    Tableau.Logger().error('Unexpected state: ' + key);
    return NavLocationAreas.Generic.unknown;
  }

  return navLocationMap[matchingStateName];
}

const reactRouterStateParamSpaceToNavLocation: _.Dictionary<NavLocation> = {
    // more specific paths have to come before their superclasses (e.g., 'datasources.askDataUsageAnalytics' could match 'datasources', so put that first)

  'favorites': NavLocationAreas.SiteContent.favorites,
  'recents': NavLocationAreas.SiteContent.recents,
  'search': NavLocationAreas.SiteContent.search,
  'workbooks': NavLocationAreas.SiteContent.explore,
  'groups': NavLocationAreas.SiteAdmin.site_groups,
  'datasources.askDataUsageAnalytics': NavLocationAreas.SiteContent.datasource_owner_panel,
  'datasources': NavLocationAreas.SiteContent.explore,
  'server.sites': NavLocationAreas.ServerAdmin.server_sites,
  'user': NavLocationAreas.SiteAdmin.site_users,
  'users': NavLocationAreas.SiteAdmin.site_users,
  'home': NavLocationAreas.SiteContent.home,
  'recommendations': NavLocationAreas.SiteContent.recommendations,
  'metric_detail': NavLocationAreas.SiteContent.metric_detail,
  'metrics': NavLocationAreas.SiteContent.metrics,
  'dataRoles': NavLocationAreas.SiteContent.explore,
  'status': NavLocationAreas.SiteAdmin.site_status,
  'projects': NavLocationAreas.SiteContent.explore,
  'views': NavLocationAreas.SiteContent.explore,
  'explore': NavLocationAreas.SiteContent.explore,
  'flows': NavLocationAreas.SiteContent.explore,
  'tasks': NavLocationAreas.SiteAdmin.site_tasks,
  'jobs': NavLocationAreas.SiteAdmin.site_jobs,
  'server.jobs' : NavLocationAreas.ServerAdmin.server_jobs,
  'server.schedules': NavLocationAreas.ServerAdmin.server_schedules,
  'server.user': NavLocationAreas.ServerAdmin.server_users,
  'schedules': NavLocationAreas.SiteAdmin.site_schedules,
  'redirect_to_favorite_view': NavLocationAreas.SiteContent.site_viz,  // only used between vizzes so this makes sure the viz header shows in this in-between state
  'redirect_to_view': NavLocationAreas.SiteContent.site_viz,  // used elsewhere, so this makes sure the correct page header shows in this in-between state and its error page
};
const reactRouterStateParamSpaceToNavLocationKeys = _.keys(reactRouterStateParamSpaceToNavLocation);

const routerStateNameToNavLocation: _.Dictionary<NavLocation> = {
  // Because most bad routes happen in regards to content items (e.g. invalid workbook ID),
  // we treat the "error" state as part of the site area. This will ensure that nav elements
  // (including the alerts menu and site picker) stay in "site mode" if the user lands on an error page.
  'main.error': NavLocationAreas.SiteContent.site_unspecified,

  // more specific paths have to come before their superclasses (e.g., 'favorites' could match 'content', so put them first)
  'main.site.content': NavLocationAreas.SiteContent.explore,
  'main.site.catalog': NavLocationAreas.SiteContent.catalog,
  'main.site.viz': NavLocationAreas.SiteContent.site_viz,

  'main.site.analysis': NavLocationAreas.SiteAdmin.site_viz,  // status vizzes
  'main.site.schedule': NavLocationAreas.SiteAdmin.site_schedules,
  'main.site.settings': NavLocationAreas.SiteAdmin.site_settings,
  'main.site.tasks': NavLocationAreas.SiteAdmin.site_tasks, // TODO: TASK 941299 remove once FeatureFlags.stylishTasks is true
  'main.site.users': NavLocationAreas.SiteAdmin.site_users, // remove with stylishSiteUsers

  'main.site': NavLocationAreas.SiteContent.site_unspecified,

  // server pages
  'main.server.analysis': NavLocationAreas.ServerAdmin.server_viz, // status vizzes
  'main.server.schedule': NavLocationAreas.ServerAdmin.server_schedules,
  'main.server.settings': NavLocationAreas.ServerAdmin.server_settings,
  'main.server.sites': NavLocationAreas.ServerAdmin.server_sites,
  'main.server.status': NavLocationAreas.ServerAdmin.server_status,
  'main.server.backgroundJobs': NavLocationAreas.ServerAdmin.server_jobs,
  'main.server.tasks': NavLocationAreas.ServerAdmin.server_tasks,
  'main.server.user': NavLocationAreas.ServerAdmin.server_users,  // remove with stylishServerUser
  'main.server.users': NavLocationAreas.ServerAdmin.server_users, // remove with stylishServerUser

  'main.server': NavLocationAreas.ServerAdmin.server_unspecified,
};

const routerStateNameToNavLocationKeys = _.keys(routerStateNameToNavLocation);
